/**
 * 关键词词云图组件
 */
import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import 'echarts-wordcloud';
import { DBO_V, DBO_EC } from '@op/utils/format';
import { sentimentColorList, sentimentType } from '@op/utils';
import { sentimentColorMap } from '@op/constants';
import { formatter } from '@op/components/ChartCommon/formatter';
import { ClassSpecial } from '@op/constants/common';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import useLanguage from '@op/hooks/useLanguage';
import { IKeywordItemWithQoq } from '@op/pages/SecurityWeeklyReport/hooks/useKeywordData';
import './WordCloudChart.scss';

interface IKeywordWordCloudChartProps {
  data: IKeywordItemWithQoq[];
  getKeywordName: (item: any) => string;
  height: number;
  filter?: any;
  /** 是否显示 Feeds 跳转按钮 */
  showFeedsFooter?: boolean;
  /** Feeds 按钮点击回调 */
  onFeedsClick?: (keyword: string) => void;
}

const KeywordWordCloudChart: React.FC<IKeywordWordCloudChartProps> = ({
  data,
  getKeywordName,
  height,
  filter,
  showFeedsFooter = false,
  onFeedsClick,
}) => {
  const { formatMessage } = useLanguage();

  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter: filter || {},
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: ({ dataAttrs }) => {
      const keyword = dataAttrs.keyword || '';
      onFeedsClick?.(keyword);
    },
  });

  // 情感类型图例配置
  const customLegends = useMemo(
    () => [
      {
        text: formatMessage({ id: 'Positive' }),
        color: sentimentColorMap[0].color,
      },
      {
        text: formatMessage({ id: 'Neutral' }),
        color: sentimentColorMap[1].color,
      },
      {
        text: formatMessage({ id: 'Negative' }),
        color: sentimentColorMap[2].color,
      },
    ],
    [formatMessage],
  );

  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    const wordData = data.slice(0, 30).map((item) => ({
      name: getKeywordName(item.keyword),
      oriName: item.keyword,
      value: item.mentions,
      // sentimentType 返回 1/2/3，需要减 1 映射到 colorIndex 0/1/2
      colorIndex: (sentimentType(item.avg_sentiment, true) as number) - 1,
      sentiment: item.avg_sentiment,
    }));

    return {
      tooltip: {
        formatter: (params: any) => {
          const { oriName, name, value, sentiment } = params.data || {}; 
          const footerText = showFeedsFooter
            ? getBottomClickBtn({ keyword: oriName })
            : undefined;
          return formatter({
            title: name,
            tables: [
              {
                showHeader: false,
                records: [
                  {
                    type: formatMessage({ id: 'Mentions' }),
                    score: DBO_V.reviews(value),
                  },
                  {
                    type: formatMessage({ id: 'Sentiment' }),
                    score: sentiment?.toFixed(2) || '-',
                  },
                ],
                columns: [
                  { key: 'type' },
                  { key: 'score', weight: 'bold', align: 'right' },
                ],
              },
            ],
            footerText,
          });
        },
        className: showFeedsFooter ? ClassSpecial.CHART_TOOLTIP_SCROLL : '',
        enterable: showFeedsFooter,
        position: showFeedsFooter ? DBO_EC.enterablePosition : undefined,
        borderWidth: 0,
        confine: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: 0,
        textStyle: {
          fontSize: 12,
        },
      },
      series: [
        {
          type: 'wordCloud',
          shape: 'circle',
          gridSize: data.length >= 25 ? 20 : 24,
          sizeRange: [12, 48],
          rotationRange: [0, 0],
          layoutAnimation: true,
          textStyle: {
            color: (param: any) => {
              return sentimentColorList[param.data.colorIndex];
            },
          },
          emphasis: {
            textStyle: {
              fontWeight: 600,
            },
          },
          height: '100%',
          drawOutOfBound: false,
          shrinkToFit: true,
          data: wordData,
        },
      ],
    };
  }, [data, getKeywordName, showFeedsFooter, getBottomClickBtn]);

  return (
    <div
      ref={divElementRef}
      className="dbo-keyword-wordcloud"
      style={{ position: 'relative' }}
    >
      <ReactEcharts
        style={{ height: height - 30, width: '100%' }}
        option={option}
        lazyUpdate={true}
        opts={{ renderer: 'svg' }}
        notMerge={true}
      />
      <div className="dbo-keyword-wordcloud__legend">
        {customLegends.map(({ text, color }, index) => (
          <span key={index} className="dbo-keyword-wordcloud__legend-item">
            <span
              className="dbo-keyword-wordcloud__legend-dot"
              style={{ backgroundColor: color }}
            />
            <span className="dbo-keyword-wordcloud__legend-text">{text}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default KeywordWordCloudChart;
